import { createContext, useContext, useEffect, useState } from "react";
import { Outlet, Link, NavLink, useLocation } from "react-router-dom";
import { PawPrint, Sun, Moon, Menu, X, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { useTheme } from "@/lib/theme";
import { DiscountTab } from "./ExitCapture";

const SiteConfigContext = createContext({});
export const useSiteConfig = () => useContext(SiteConfigContext);

const NAV = [
  { to: "/preventivo", label: "Preventivo rapido", id: "calculator" },
  { to: "/confronta", label: "Confronta polizze", id: "confronta" },
  { to: "/razze", label: "Razze", id: "razze" },
  { to: "/blog", label: "Guide", id: "blog" },
  { to: "/chi-siamo", label: "Chi siamo", id: "chi-siamo" },
  { to: "/contatti", label: "Contatti", id: "contatti" },
];

function injectHeadTags(html) {
  document.querySelectorAll("[data-pp-tag]").forEach((n) => n.remove());
  if (!html) return;
  const tpl = document.createElement("template");
  tpl.innerHTML = html.trim();
  Array.from(tpl.content.childNodes).forEach((node) => {
    if (node.nodeType !== 1 || !["META", "LINK"].includes(node.tagName)) return;
    const el = document.createElement(node.tagName);
    Array.from(node.attributes).forEach((a) => { if (!/^on/i.test(a.name) && !/^\s*javascript:/i.test(a.value)) el.setAttribute(a.name, a.value); });
    el.setAttribute("data-pp-tag", "1");
    document.head.appendChild(el);
  });
}

export function Header() {
  const { theme, toggle } = useTheme();
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 glass" data-testid="site-header">
      <div className="mx-auto max-w-7xl px-4 lg:px-8 py-3.5 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2.5 font-display font-bold text-lg" data-testid="site-logo">
          <span className="grid place-items-center h-9 w-9 rounded-xl bg-emerald-500 text-white"><PawPrint size={20} /></span>
          Polizzapet<span className="text-emerald-500">.it</span>
        </Link>
        <nav className="hidden md:flex items-center gap-1" aria-label="Principale">
          {NAV.map((n) => (
            <NavLink key={n.id} to={n.to} data-testid={`nav-link-${n.id}`}
              className="px-3.5 py-2 rounded-full text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <button onClick={toggle} aria-label="Cambia tema" data-testid="theme-toggle-button"
            className="h-9 w-9 grid place-items-center rounded-full border border-border hover:bg-muted transition-colors">
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <Link to="/consulenza" className="hidden sm:inline-flex btn-primary !py-2 !px-4 text-sm" data-testid="header-cta-consulenza">Consulenza gratuita</Link>
          <button className="md:hidden h-9 w-9 grid place-items-center rounded-full border border-border" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="mobile-menu-button">
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-border px-4 py-3 flex flex-col gap-1 bg-background" data-testid="mobile-menu">
          {NAV.map((n) => (
            <Link key={n.id} to={n.to} onClick={() => setOpen(false)} className="px-3 py-2.5 rounded-lg hover:bg-muted font-medium" data-testid={`mobile-nav-${n.id}`}>{n.label}</Link>
          ))}
          <Link to="/consulenza" onClick={() => setOpen(false)} className="btn-primary mt-2" data-testid="mobile-cta-consulenza">Consulenza gratuita</Link>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  const cfg = useSiteConfig();
  return (
    <footer className="border-t border-border mt-24" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 lg:px-8 py-14 grid gap-10 md:grid-cols-4">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2 font-display font-bold text-lg mb-3">
            <PawPrint className="text-emerald-500" size={22} /> Polizzapet.it
          </div>
          <p className="text-sm text-muted-foreground max-w-md leading-relaxed">
            Ragazzi animalisti che leggono le polizze pet al posto tuo. Confronti onesti, preventivo gratuito e consulenza senza impegno.
            Alcuni link sono di affiliazione: se attivi una polizza tramite noi riceviamo una commissione, tu non paghi nulla in più.
          </p>
          <a href={`mailto:${cfg.contact_email || "info@polizzapet.it"}`} className="inline-flex items-center gap-2 mt-4 text-sm text-emerald-500 hover:underline" data-testid="footer-email">
            <Mail size={14} /> {cfg.contact_email || "info@polizzapet.it"}
          </a>
        </div>
        <div>
          <p className="label-mono mb-3">Esplora</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/preventivo" className="hover:text-emerald-500">Preventivo rapido</Link></li>
            <li><Link to="/confronta" className="hover:text-emerald-500">Confronta polizze</Link></li>
            <li><Link to="/razze" className="hover:text-emerald-500">Assicurazione per razza</Link></li>
            <li><Link to="/citta" className="hover:text-emerald-500">Costi veterinari per città</Link></li>
            <li><Link to="/blog" className="hover:text-emerald-500">Guide</Link></li>
            <li><Link to="/chi-siamo" className="hover:text-emerald-500">Chi siamo</Link></li>
            <li><Link to="/consulenza" className="hover:text-emerald-500">Consulenza gratuita</Link></li>
            <li><Link to="/contatti" className="hover:text-emerald-500">Contatti</Link></li>
            <li><Link to="/login" className="hover:text-emerald-500 text-muted-foreground" data-testid="footer-login">Area riservata</Link></li>
          </ul>
        </div>
        <div>
          <p className="label-mono mb-3">Legale</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/privacy" className="hover:text-emerald-500" data-testid="footer-privacy">Privacy Policy</Link></li>
            <li><Link to="/cookie" className="hover:text-emerald-500" data-testid="footer-cookie">Cookie Policy</Link></li>
            <li><Link to="/note-legali" className="hover:text-emerald-500">Note Legali</Link></li>
            <li><Link to="/trasparenza" className="hover:text-emerald-500">Trasparenza</Link></li>
            <li><Link to="/informativa-clienti" className="hover:text-emerald-500">Informativa Clienti</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-border">
        <div className="mx-auto max-w-7xl px-4 lg:px-8 py-5 flex flex-col sm:flex-row justify-between gap-2 text-xs text-muted-foreground">
          <span>© {new Date().getFullYear()} Polizzapet.it — Tutti i diritti riservati</span>
          <span>Polizzapet.it non è una compagnia assicurativa: fornisce informazioni e confronti.</span>
        </div>
      </div>
    </footer>
  );
}

export function CookieBanner() {
  const [show, setShow] = useState(() => !localStorage.getItem("pp_cookie"));
  if (!show) return null;
  const choose = (v) => { localStorage.setItem("pp_cookie", v); setShow(false); };
  return (
    <div className="fixed bottom-3 inset-x-3 sm:bottom-4 sm:left-auto sm:right-6 sm:max-w-md z-[60] surface p-3 sm:p-5 shadow-2xl reveal" role="dialog" data-testid="cookie-banner">
      <p className="text-xs sm:text-sm text-muted-foreground leading-snug">
        <b className="text-foreground">Cookie:</b> solo tecnici e, se accetti, statistici anonimi. Nessuna pubblicità. <Link to="/cookie" className="underline">Dettagli</Link>
      </p>
      <div className="flex gap-2 mt-2 sm:mt-4">
        <button className="btn-primary !py-1.5 sm:!py-2 text-xs sm:text-sm flex-1" onClick={() => choose("all")} data-testid="cookie-accept-all">Accetta</button>
        <button className="btn-ghost !py-1.5 sm:!py-2 text-xs sm:text-sm flex-1" onClick={() => choose("necessary")} data-testid="cookie-necessary-only">Solo necessari</button>
      </div>
    </div>
  );
}

export default function SiteLayout() {
  const [cfg, setCfg] = useState({});
  const location = useLocation();

  useEffect(() => {
    api.get("/public/site-config").then((r) => { setCfg(r.data); injectHeadTags(r.data.head_tags); }).catch(() => {});
  }, []);

  useEffect(() => {
    api.post("/public/track", { path: location.pathname, referrer: document.referrer || null }).catch(() => {});
    if (location.hash) {
      setTimeout(() => document.querySelector(location.hash)?.scrollIntoView({ behavior: "smooth" }), 80);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <SiteConfigContext.Provider value={cfg}>
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1"><Outlet /></main>
        <Footer />
        <CookieBanner />
        <DiscountTab />
      </div>
    </SiteConfigContext.Provider>
  );
}
